import React, { useState, useMemo, useCallback } from 'react'
import { FixedSizeList as List } from 'react-window'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../../components/ui/dropdown-menu'
import { Input } from '../../../components/ui/input'
import * as Icon from 'lucide-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import axios from '../../../lib/axios'
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../../components/ui/alert-dialog'
import { Button } from '../../../components/ui/button'
import { isAxiosError } from 'axios'
import toast from 'react-hot-toast'

const DeleteUser = ({trigger, userId}:{trigger:React.ReactNode, userId:number}) => {
  const [open, setOpen] = React.useState(false)

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await axios.delete(`users?userid=${userId}`)
    },
    onError: (error) => {
      if(isAxiosError(error)) {
        toast.error(error.response?.data?.message)
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('Er is een onbekende fout opgetreden.')
      }
    },
    onSuccess: () => {
      toast.success('Gebruiker verwijderd.')
      setOpen(false)
    }
  })
  
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Ben je zeker?</AlertDialogTitle>
          <AlertDialogDescription>Deze actie kan niet ongedaan worden gemaakt.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuleren</AlertDialogCancel>
          <Button 
            variant='destructive'
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending && <Icon.Loader className='w-4 h-4 animate-spin' />}
            Verwijderen
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// Memoized row component to prevent unnecessary re-renders
const UserRow = React.memo(({ index, style, data }:{ index: number, style: React.CSSProperties, data:{users:User[]} }) => {
  const user = data.users[index]

  return (
    <div style={style} className="flex items-center border-b border-white/10">
      <div className="flex items-center w-full">
        <div className="flex-1 px-4 py-2 text-sm">{user.m_sFirstName}</div>
        <div className="flex-1 px-4 py-2 text-sm">{user.m_sLastName}</div>
        <div className="flex-1 px-4 py-2 text-sm">{user?.m_sEmail}</div>
        <div className="flex justify-end w-16 px-4 py-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded hover:bg-white/10">
                <Icon.MoreHorizontal className='w-4 h-4' />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DeleteUser 
                userId={user.m_iUserID}
                trigger={
                  <DropdownMenuItem 
                    onSelect={(e)=>{e.preventDefault()}}
                    className='flex items-center gap-2 text-destructive hover:!text-destructive/80'
                  >
                    <Icon.Trash className='w-4 h-4' />
                    Verwijderen
                  </DropdownMenuItem>
                }
              />
              
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
})

UserRow.displayName = 'UserRow'

const UsersPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const { data } = await axios.get('users')
      return data
    }
  })

  // Memoized filtered users to prevent recalculation on every render
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users
    
    const searchLower = searchTerm.toLowerCase()
    return users.filter(user => 
      user.m_sFirstName?.toLowerCase().includes(searchLower) ||
      user.m_sLastName?.toLowerCase().includes(searchLower) ||
      `${user.m_sFirstName} ${user.m_sLastName}`.toLowerCase().includes(searchLower) ||
      user.m_sEmail?.toLowerCase().includes(searchLower)
    )
  }, [users, searchTerm])

  // Memoized item data to prevent recreating object on every render
  const itemData = useMemo(() => ({
    users: filteredUsers,
  }), [filteredUsers])

  const ROW_HEIGHT = 52 // Height of each row in pixels
  const MAX_HEIGHT = 600 // Maximum height of the virtual list

  return (
    <div className='h-full gap-4 py-4'>
      <div className='flex flex-col justify-between h-full gap-4'>
        {/* Search input */}
        <div className='flex items-center justify-between'>
          <Input
            placeholder="Zoek gebruikers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="text-white border bg-white/10 border-white/20"
          />
        </div>

        <div className='h-full overflow-hidden border border-white/20'>
          <div className='flex flex-col justify-between h-full'>
            {/* Table Header */}
            <div className="bg-muted/10">
              <div className="flex items-center py-3 border-b border-white/20">
                <div className="flex-1 px-4 text-sm font-medium text-white">Voornaam</div>
                <div className="flex-1 px-4 text-sm font-medium text-white">Achternaam</div>
                <div className="flex-1 px-4 text-sm font-medium text-white">E-mail</div>
                <div className="w-16 px-4"></div>
              </div>
            </div>

            {/* Virtualized Table Body */}
            <div className="h-full overflow-y-auto bg-background/5">
              {filteredUsers.length > 0 ? (
                <List
                  height={Math.min(filteredUsers.length * ROW_HEIGHT, MAX_HEIGHT)}
                  itemCount={filteredUsers.length}
                  itemSize={ROW_HEIGHT}
                  itemData={itemData}
                  className="!h-full scrollbar-thin"
                >
                  {UserRow}
                </List>
              ) : (
                <div className="flex items-center justify-center py-8 text-gray-400">
                  {isLoading 
                  ? 'Laden...' 
                  :
                  <>
                  {searchTerm ? 'Geen gebruikers gevonden' : 'Geen gebruikers beschikbaar'}
                  </>
                  }
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Results counter */}
        <div className="text-sm text-gray-400">
          {filteredUsers.length} van {users.length} gebruikers
        </div>
      </div>
    </div>
  )
}

export default UsersPage